import React, { useState } from 'react';
import { Form, Button, Card, Spinner, Alert, Table, Badge, Modal } from 'react-bootstrap';
import api from '../../api';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import styles from './styles.module.css';

interface EditalSiga {
  editalId: string;
  nome: string;
}

interface ProcessoSiga {
  processoId: string;
  identificador: string;
  dataCriacao: string;
  editais: EditalSiga[];
}

interface ResultadoSiga {
  nome: string;
  cpf: string;
  status: string;
  inscricao: string;
}

interface InscricaoDesistida {
  id: string;
  bancaId: string;
  processoIdentificador: string;
  erroMensagem: string;
  createdAt: string;
  requestPayload: string;
}

const SigaDac: React.FC = () => {
  const [processos, setProcessos] = useState<ProcessoSiga[]>([]);
  const [processoSelecionado, setProcessoSelecionado] = useState<string>('');
  const [loadingProcessos, setLoadingProcessos] = useState(true);
  const [tipoBusca, setTipoBusca] = useState<'cpf' | 'inscricao'>('cpf');
  const [cpf, setCpf] = useState('');
  const [inscricao, setInscricao] = useState('');
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<ResultadoSiga | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [animateResult, setAnimateResult] = useState(false);
  const resultadoRef = React.useRef<HTMLDivElement>(null);
  const [inscricoesDesistidas, setInscricoesDesistidas] = useState<InscricaoDesistida[]>([]);
  const [loadingDesistidos, setLoadingDesistidos] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [resultadoModal, setResultadoModal] = useState<ResultadoSiga | null>(null);
  const [loadingModal, setLoadingModal] = useState(false);
  const [inscricaoSelecionada, setInscricaoSelecionada] = useState('');

  React.useEffect(() => {
    const carregarProcessos = async () => {
      try {
        const response = await api.get<ProcessoSiga[]>('/integracao-siga/processos');
        setProcessos(response.data);
        if (response.data.length > 0) {
          setProcessoSelecionado(response.data[0].processoId);
        }
      } catch (err: any) {
        setError(BackendErrorMessage(err));
      } finally {
        setLoadingProcessos(false);
      }
    };
    carregarProcessos();
    carregarInscricoesDesistidas();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResultado(null);

    if (!processoSelecionado) {
      setError('Selecione um processo');
      return;
    }

    if (tipoBusca === 'cpf' && (!cpf || cpf.length < 11)) {
      setError('CPF inválido');
      return;
    }

    if (tipoBusca === 'inscricao' && !inscricao) {
      setError('Inscrição é obrigatória');
      return;
    }

    try {
      setLoading(true);
      const params: any = { processoId: processoSelecionado };
      if (tipoBusca === 'cpf') {
        params.cpf = cpf.replace(/\D/g, '');
      } else {
        params.inscricao = inscricao;
      }
      
      const response = await api.get<ResultadoSiga>('/integracao-siga/candidatos/resultado', { params });
      setResultado(response.data);
      setAnimateResult(true);
      setTimeout(() => {
        resultadoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      setTimeout(() => setAnimateResult(false), 2000);
    } catch (err: any) {
      setError(BackendErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return value;
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setCpf(formatted);
  };

  const processoAtual = processos.find(p => p.processoId === processoSelecionado);

  const carregarInscricoesDesistidas = async () => {
    try {
      setLoadingDesistidos(true);
      const response = await api.get('/integracao-siga/logs/desistidos', {
        params: { size: 100 }
      });
      setInscricoesDesistidas(response.data.content || []);
    } catch (err: any) {
      console.error('Erro ao carregar inscrições desistidas:', err);
    } finally {
      setLoadingDesistidos(false);
    }
  };

  const extrairInscricao = (payload: string): string => {
    try {
      const match = payload.match(/"inscricao":\s*"([^"]+)"/);
      return match ? match[1] : 'N/A';
    } catch {
      return 'N/A';
    }
  };

  const consultarInscricao = async (inscricao: string, processoId: string) => {
    setInscricaoSelecionada(inscricao);
    setShowModal(true);
    setLoadingModal(true);
    setResultadoModal(null);

    try {
      const response = await api.get<ResultadoSiga>('/integracao-siga/candidatos/resultado', {
        params: { processoId, inscricao }
      });
      setResultadoModal(response.data);
    } catch (err: any) {
      console.error('Erro ao consultar inscrição:', err);
    } finally {
      setLoadingModal(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Consulta para usuários SIGA/DAC</h1>
        <p className={styles.subtitle}>Consulte o estado mais atualizado dos Candidatos do Processo em andamento</p>
      </div>

      <Card className={styles.card}>
        <Card.Body>
          {loadingProcessos ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Carregando processos...</p>
            </div>
          ) : processos.length === 0 ? (
            <Alert variant="warning">Nenhum processo com integração SIGA encontrado</Alert>
          ) : (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Processo</Form.Label>
                <Form.Select
                  value={processoSelecionado}
                  onChange={(e) => setProcessoSelecionado(e.target.value)}
                  disabled={loading}
                >
                  {processos.map((processo) => (
                    <option key={processo.processoId} value={processo.processoId}>
                      {processo.identificador}
                    </option>
                  ))}
                </Form.Select>
                {processoAtual && processoAtual.editais.length > 0 && (
                  <Form.Text className="text-muted">
                    Editais: {processoAtual.editais.map(e => e.nome).join(', ')}
                  </Form.Text>
                )}
              </Form.Group>

              <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Tipo de Busca</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check
                  type="radio"
                  label="CPF"
                  checked={tipoBusca === 'cpf'}
                  onChange={() => setTipoBusca('cpf')}
                  disabled={loading}
                />
                <Form.Check
                  type="radio"
                  label="Inscrição"
                  checked={tipoBusca === 'inscricao'}
                  onChange={() => setTipoBusca('inscricao')}
                  disabled={loading}
                />
              </div>
            </Form.Group>

            {tipoBusca === 'cpf' ? (
              <Form.Group className="mb-3">
                <Form.Label>CPF do Candidato</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="000.000.000-00"
                  value={cpf}
                  onChange={handleCpfChange}
                  maxLength={14}
                  disabled={loading}
                />
              </Form.Group>
            ) : (
              <Form.Group className="mb-3">
                <Form.Label>Inscrição do Candidato</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Digite a inscrição"
                  value={inscricao}
                  onChange={(e) => setInscricao(e.target.value)}
                  disabled={loading}
                />
              </Form.Group>
            )}

            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading || (tipoBusca === 'cpf' ? !cpf : !inscricao)}
              className={styles.submitButton}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Consultando...
                </>
              ) : (
                'Consultar'
              )}
            </Button>
              </Form>
            </>
          )}

          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}

          {resultado && (
            <div 
              ref={resultadoRef} 
              className={`${styles.resultContainer} ${animateResult ? styles.animate : ''}`}
            >
              <h5>Resultado da Consulta</h5>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div className={`${styles.resultBadge} ${resultado.status.toUpperCase() === 'VALIDADO' ? styles.validado : styles.naoValidado}`}>
                  {resultado.status}
                </div>
              </div>
              <div className={styles.resultGrid}>
                <div className={styles.resultItem}>
                  <strong>CPF:</strong>
                  <span>{resultado.cpf}</span>
                </div>
                <div className={styles.resultItem}>
                  <strong>Nome:</strong>
                  <span>{resultado.nome}</span>
                </div>
                <div className={styles.resultItem}>
                  <strong>Inscrição:</strong>
                  <span>{resultado.inscricao}</span>
                </div>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {inscricoesDesistidas.length > 0 && (
        <Card className={`${styles.card} mt-4`}>
          <Card.Body>
            <h5 className="mb-3">
              <Badge bg="warning" text="dark">Inscrições Problemáticas</Badge>
              <span className="ms-2 text-muted" style={{ fontSize: '0.9rem' }}>({inscricoesDesistidas.length})</span>
            </h5>
            <p className="text-muted small">Inscrições que não puderam ser enviadas ao SIGA por conterem caracteres inválidos</p>
            {loadingDesistidos ? (
              <div className="text-center py-3">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              <div className={styles.tableContainer}>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Inscrição</th>
                      <th>Processo</th>
                      <th>Motivo</th>
                      <th>Data</th>
                      <th>Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inscricoesDesistidas.map((item) => {
                      const inscricaoExtraida = extrairInscricao(item.requestPayload);
                      const processoId = processos.find(p => p.identificador === item.processoIdentificador)?.processoId;
                      return (
                        <tr key={item.id}>
                          <td><code>{inscricaoExtraida}</code></td>
                          <td>{item.processoIdentificador}</td>
                          <td className="text-danger small">{item.erroMensagem}</td>
                          <td>{new Date(item.createdAt).toLocaleString('pt-BR')}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => processoId && consultarInscricao(inscricaoExtraida, processoId)}
                              disabled={!processoId}
                            >
                              Verificar
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Resultado mais atualizado no Identifica da Inscrição: <code>{inscricaoSelecionada}</code></Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingModal ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Carregando dados...</p>
            </div>
          ) : resultadoModal ? (
            <>
              <Alert variant={resultadoModal.status.toUpperCase() === 'VALIDADO' ? 'success' : 'danger'}>
                <strong>Status:</strong> {resultadoModal.status}
              </Alert>
              <div className="mb-3">
                <strong>Nome:</strong> {resultadoModal.nome}
              </div>
              <div className="mb-3">
                <strong>CPF:</strong> {resultadoModal.cpf}
              </div>
              <div className="mb-3">
                <strong>Inscrição:</strong> <code>{resultadoModal.inscricao}</code>
              </div>
              
            </>
          ) : (
            <Alert variant="danger">Não foi possível carregar os dados do candidato.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SigaDac;
